<template>
  <div class="image-cropper-component w-100">
    <!-- Input padrão do sistema -->
    <input 
      type="file" 
      accept="image/*" 
      @change="onFileChange" 
      class="form-control form-control-pmg" 
      ref="fileInputRef"
    />

    <!-- MODAL DE CORTE -->
    <div v-if="showModal" class="cropper-modal-overlay" @click.self="cancelCrop">
      <div class="cropper-modal-content shadow-lg rounded-4 bg-white p-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="fw-bold mb-0 d-flex align-items-center" style="color: #561569;">
            <i class="bi bi-crop fs-4 me-2 text-warning"></i> Ajustar Enquadramento
          </h5>
          <button type="button" class="btn-close" @click="cancelCrop"></button>
        </div>

        <p class="text-muted small mb-3">
          Arraste a imagem para centralizar e use os botões abaixo (ou o scroll do mouse) para dar zoom.
        </p>

        <!-- Container do Cropper -->
        <div :class="['cropper-wrapper mb-3', { 'is-round': isRound }]">
          <img ref="imageRef" :src="imageUrl" alt="Imagem para corte" style="max-width: 100%; display: block;" />
        </div>

        <!-- Botões de Controle de Zoom -->
        <div class="d-flex justify-content-center gap-2 mb-4">
          <button type="button" class="btn btn-sm btn-light border shadow-sm px-3 text-secondary" @click="zoomOut" title="Afastar">
            <i class="bi bi-zoom-out fs-6"></i>
          </button>
          <button type="button" class="btn btn-sm btn-light border shadow-sm px-3 text-secondary" @click="resetZoom" title="Tamanho Original">
            <i class="bi bi-arrow-counterclockwise fs-6"></i>
          </button>
          <button type="button" class="btn btn-sm btn-light border shadow-sm px-3 text-secondary" @click="zoomIn" title="Aproximar">
            <i class="bi bi-zoom-in fs-6"></i>
          </button>
        </div>

        <!-- Botões de Ação no Padrão do Sistema -->
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-pmg-light" @click="cancelCrop">
            <i class="bi bi-ban"></i> Cancelar
          </button>
          <button type="button" class="btn btn-pmg-primary d-flex align-items-center" @click="cortarImagem">
            <i class="bi bi-check-lg me-1"></i> Confirmar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-env vue/setup-compiler-macros */
import { ref, nextTick, onUnmounted } from 'vue';

const props = defineProps({
  aspectRatio: { type: Number, default: 1 },
  isRound: { type: Boolean, default: false }
});

const emit = defineEmits(['cropped']);

const fileInputRef = ref(null);
const imageUrl = ref(null);
const imageRef = ref(null);
const showModal = ref(false);
let cropper = null;

const onFileChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  if (imageUrl.value) {
    URL.revokeObjectURL(imageUrl.value);
  }

  imageUrl.value = URL.createObjectURL(file);
  showModal.value = true;

  nextTick(() => {
    setTimeout(() => {
      if (imageRef.value && window.Cropper) {
        if (cropper) {
          cropper.destroy();
        }
        cropper = new window.Cropper(imageRef.value, {
          aspectRatio: props.aspectRatio,
          viewMode: 1,
          dragMode: 'move',
          cropBoxResizable: false,
          toggleDragModeOnDblclick: false,
          responsive: true,
          // Ativando o zoom por scroll/mouse
          zoomOnWheel: true,
          zoomOnTouch: true,
          wheelZoomRatio: 0.1,
        });
      }
    }, 150);
  });
};

// Funções de controle de Zoom
const zoomIn = () => {
  if (cropper) cropper.zoom(0.1);
};

const zoomOut = () => {
  if (cropper) cropper.zoom(-0.1);
};

const resetZoom = () => {
  if (cropper) cropper.reset();
};

const cortarImagem = () => {
  if (!cropper || typeof cropper.getCroppedCanvas !== 'function') {
    console.error("Instância do Cropper inválida.");
    return;
  }

  const canvas = cropper.getCroppedCanvas({
    maxWidth: 1920,
    maxHeight: 1920,
  });

  if (!canvas) {
    console.error("Não foi possível gerar o canvas recortado.");
    return;
  }

  canvas.toBlob((blob) => {
    if (blob) {
      emit('cropped', blob);
      closeModal();
    }
  }, 'image/jpeg', 0.9);
};

const cancelCrop = () => {
  closeModal();
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const closeModal = () => {
  showModal.value = false;
  if (cropper) {
    cropper.destroy();
    cropper = null;
  }
  if (imageUrl.value) {
    URL.revokeObjectURL(imageUrl.value);
    imageUrl.value = null;
  }
};

onUnmounted(() => {
  closeModal();
});
</script>

<style scoped>
.cropper-wrapper {
  width: 100%;
  height: 350px;
  background-color: #f3f4f6;
  overflow: hidden;
  border-radius: 8px;
}

.cropper-wrapper img {
  max-width: 100%;
}

.cropper-wrapper.is-round :deep(.cropper-view-box),
.cropper-wrapper.is-round :deep(.cropper-face) {
  border-radius: 50%;
}

.cropper-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.7);
  z-index: 1070;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

.cropper-modal-content {
  width: 100%;
  max-width: 550px;
  margin: 0 20px;
}

.btn-pmg-primary {
    background-color: #561569;
    color: white;
    border: none;
    transition: all 0.3s ease;
}

.btn-pmg-primary:hover:not(:disabled) {
    background-color: #360c42;
    transform: translateY(-1px);
}

.btn-pmg-primary:disabled {
    background-color: #A5ADC1;
    cursor: not-allowed;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    padding: 10px 20px;
}
</style>